import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import type { TokenResponse, UserResponse, Department, Employee, Contract, Project, Task, Assignment, Notification, StatusValue, WorkType } from '../types';
import { readCsrfToken, shouldAttachCsrf } from './csrf';

// Use same-origin (relative) URLs when on production, preview URL only during development
const envUrl = process.env.REACT_APP_BACKEND_URL || '';
const isProduction = typeof window !== 'undefined' && !window.location.hostname.includes('preview.emergentagent.com') && window.location.hostname !== 'localhost';
const API_BASE_URL: string = isProduction ? window.location.origin : (envUrl || window.location.origin);

const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// httpOnly cookies handle auth automatically via withCredentials
// #44 — Inject the CSRF token (read from non-httpOnly cookie) on writes
// so the double-submit middleware lets the request through.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (shouldAttachCsrf(config.method)) {
    const csrf = readCsrfToken();
    if (csrf) {
      config.headers.set('X-CSRF-Token', csrf);
    }
  }
  return config;
});

// Handle auth errors with automatic token refresh
let isRefreshing = false;
type QueueItem = { resolve: (token: string | null) => void; reject: (error: unknown) => void };
let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 409 Conflict (optimistic concurrency)
    if (error.response?.status === 409) {
      const detail = error.response.data?.detail;
      if (detail?.error === 'version_conflict') {
        error.isVersionConflict = true;
        error.conflictDetail = detail;
      }
      return Promise.reject(error);
    }
    
    // Handle 401 with refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      const currentPath = window.location.pathname;
      if (currentPath === '/login' || currentPath === '/sso-callback') {
        return Promise.reject(error);
      }
      
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      // Attempt refresh — cookies carry the refresh_token automatically
      try {
        const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`,
          {},
          { withCredentials: true }
        );
        const { access_token } = response.data;
        // Clear any stale localStorage tokens from previous auth system
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        
        processQueue(null, access_token);
        // Set header for the retried request (backward compat for this single request)
        if (access_token) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login?error=session_expired';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth APIs
export const authAPI = {
  login: (email: string, password: string): Promise<AxiosResponse<TokenResponse>> => api.post('/auth/login', { email, password }),
  register: (data: Record<string, unknown>): Promise<AxiosResponse<UserResponse>> => api.post('/auth/register', data),
  me: (): Promise<AxiosResponse<UserResponse>> => api.get('/auth/me'),
  refresh: (refreshToken?: string): Promise<AxiosResponse<TokenResponse>> => api.post('/auth/refresh', { refresh_token: refreshToken }),
  logout: (): Promise<AxiosResponse<{ message: string }>> => api.post('/auth/logout'),
  // #48 — Self-service password change
  changePassword: (current_password: string, new_password: string, confirm_password: string) =>
    api.post('/auth/change-password', { current_password, new_password, confirm_password }),
};

// #48-ext — Admin security tooling
export const adminSecurityAPI = {
  generateJwtSecret: () => api.post('/admin/security/generate-jwt-secret'),
};

// User APIs
export const userAPI = {
  list: (params?) => api.get('/users', { params }),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  createWithEmployee: (data) => api.post('/users/create-with-employee', data),
  getActivity: (id) => api.get(`/users/${id}/activity`),
};

// Department APIs
export const departmentAPI = {
  list: (params?) => api.get('/departments', { params }),
  create: (data) => api.post('/departments', data),
  get: (id) => api.get(`/departments/${id}`),
  update: (id, data) => api.patch(`/departments/${id}`, data),
};

// Employee APIs
export const employeeAPI = {
  list: (params?) => api.get('/employees', { params }),
  create: (data) => api.post('/employees', data),
  get: (id) => api.get(`/employees/${id}`),
  update: (id, data) => api.patch(`/employees/${id}`, data),
  getWithCapacity: (params) => api.get('/employees/capacity', { params }),
  getWorkload: (id, params) => api.get(`/employees/${id}/workload`, { params }),
  getTeamCapacity: (params) => api.get('/employees/team-capacity', { params }),
  findDuplicates: () => api.get('/employees/admin/find-duplicates'),
  merge: (data: { keep_id: string; merge_id: string }) => api.post('/employees/admin/merge', data),
  deletePermanent: (id: string) => api.delete(`/employees/${id}/permanent`),
};

// Contract APIs
export const contractAPI = {
  list: (params?) => api.get('/contracts', { params }),
  create: (data) => api.post('/contracts', data),
  get: (id) => api.get(`/contracts/${id}`),
  update: (id, data) => api.patch(`/contracts/${id}`, data),
};

// Project APIs
export const projectAPI = {
  list: (params?) => api.get('/projects', { params }),
  /**
   * Fetch ALL projects matching `params` by paging through the
   * backend's 500-row cap on `/projects`. Use this instead of
   * `list({ limit: 1000 })` — the backend now rejects `limit > 500`,
   * so calling `list` with a high limit either silently caps at 500
   * (oldest behaviour) or fails outright (#85 hardening). Pages are
   * fetched serially with growing `skip`; we stop the moment a page
   * returns fewer than `PAGE_SIZE` rows OR we hit the hard cap, so a
   * runaway loop is impossible even if the backend returns exactly
   * `PAGE_SIZE` rows forever.
   *
   * Optional filter params (`contract_id`, `status`, sort, …) are
   * forwarded verbatim on every page so the filter applies across
   * the whole result set.
   */
  listAll: async (params?: Record<string, unknown>): Promise<AxiosResponse<Project[]>> => {
    const PAGE_SIZE = 500;
    // 20 pages × 500 = 10 000 projects ceiling. The real DB never
    // approaches this; the cap is purely a safety belt.
    const MAX_PAGES = 20;
    const all: Project[] = [];
    let lastResp: AxiosResponse<Project[]> | undefined;
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const skip = page * PAGE_SIZE;
      const resp = await api.get<Project[]>('/projects', {
        params: { ...(params || {}), limit: PAGE_SIZE, skip },
      });
      lastResp = resp;
      const rows = resp.data || [];
      all.push(...rows);
      if (rows.length < PAGE_SIZE) break; // last page reached
    }
    // Mimic the AxiosResponse shape callers expect from `list()`.
    return { ...(lastResp as AxiosResponse<Project[]>), data: all };
  },
  create: (data) => api.post('/projects', data),
  get: (id) => api.get(`/projects/${id}`),
  update: (id, data) => api.patch(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),  // Issue #1: Delete project
  normalizeNumbers: () => api.post('/projects/normalize-numbers'),
  // Bulk operations (Admin only)
  bulkUpdate: (projectIds, updates) => api.post('/projects/bulk-update', { project_ids: projectIds, updates }),
  bulkDelete: (projectIds) => api.post('/projects/bulk-delete', { project_ids: projectIds }),
  export: (params?) => api.get('/projects/export', { params }),
};

// Task APIs
export const taskAPI = {
  list: (params?: Record<string, unknown>): Promise<AxiosResponse<Task[]>> => api.get('/tasks', { params }),
  create: (data: Record<string, unknown>): Promise<AxiosResponse<Task>> => api.post('/tasks', data),
  get: (id: string): Promise<AxiosResponse<Task>> => api.get(`/tasks/${id}`),
  update: (id: string, data: Record<string, unknown>): Promise<AxiosResponse<Task>> => api.patch(`/tasks/${id}`, data),
  delete: (id: string) => api.delete(`/tasks/${id}`),
  updateDates: (id, dates) => api.patch(`/tasks/${id}/dates`, dates),
  // Project Lead Management (Admin)
  getUnlinkedLeads: () => api.get('/tasks/admin/unlinked-leads'),
  getLinkedLeads: () => api.get('/tasks/admin/linked-leads'),
  linkLead: (leadName, employeeId) => api.post(`/tasks/admin/link-lead?lead_name=${encodeURIComponent(leadName)}&employee_id=${employeeId}`),
  unlinkLeads: (taskIds) => api.post(`/tasks/admin/unlink-lead?${taskIds.map(id => `task_ids=${id}`).join('&')}`),
  updateTaskLead: (taskId, employeeId) => api.patch(`/tasks/admin/task/${taskId}/lead?employee_id=${employeeId || ''}`),
  normalizeLeadNames: (aliases: Record<string, string>) => api.post('/tasks/admin/normalize-lead-names', { aliases }),
  // Bulk operations (Admin only)
  bulkUpdate: (taskIds, updates) => api.post('/tasks/bulk-update', { task_ids: taskIds, updates }),
  bulkDelete: (taskIds) => api.post('/tasks/bulk-delete', { task_ids: taskIds }),
  export: (params?) => api.get('/tasks/export', { params }),
};

// Assignment APIs
export const assignmentAPI = {
  list: (params?) => api.get('/assignments', { params }),
  create: (data) => api.post('/assignments', data),
  get: (id) => api.get(`/assignments/${id}`),
  update: (id, data) => api.patch(`/assignments/${id}`, data),
  updateDates: (id, dates) => api.patch(`/assignments/${id}/dates`, dates),
  delete: (id) => api.delete(`/assignments/${id}`),
};

// Notification APIs
export const notificationAPI = {
  list: (params?: Record<string, unknown>): Promise<AxiosResponse<Notification[]>> => api.get('/notifications', { params }),
  markRead: (id: string): Promise<AxiosResponse<Notification>> => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/mark-all-read'),
};

// Status Value APIs
export const statusValueAPI = {
  list: (params?) => api.get('/status-values', { params }),
  create: (data) => api.post('/status-values', data),
  update: (id, data) => api.patch(`/status-values/${id}`, data),
  delete: (id) => api.delete(`/status-values/${id}`),  // P3 #17
};

// Dashboard APIs
export const dashboardAPI = {
  personal: (params?) => api.get('/me/dashboard', { params }),
  company: () => api.get('/admin/dashboard'),
};

// View APIs
export const viewAPI = {
  workforceGantt: (params?) => api.get('/views/workforce-gantt', { params }),
  workloadHeatmap: (params?) => api.get('/views/workload-heatmap', { params }),
};

// Work Types API
export const workTypeAPI = {
  list: (params?) => api.get('/work-types', { params }),
  create: (data) => api.post('/work-types', data),
  update: (id, data) => api.patch(`/work-types/${id}`, data),
  delete: (id) => api.delete(`/work-types/${id}`),
};

// User Preferences API (column customization)
export const userPreferencesAPI = {
  get: (page) => api.get(`/user-preferences/${page}`),
  update: (page, data) => api.put(`/user-preferences/${page}`, data),
  reset: (page) => api.post(`/user-preferences/${page}/reset`),
  getDefaults: (page) => api.get(`/user-preferences/${page}/defaults`),
  getDashboardFilters: () => api.get('/user-preferences/dashboard/filters'),
  saveDashboardFilters: (data) => api.put('/user-preferences/dashboard/filters', data),
};

// Import APIs
export const importAPI = {
  uploadExcel: (formData) => api.post('/import/excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadTemplate: () => api.get('/import/template', { responseType: 'blob' }),
  setupDepartments: () => api.post('/import/setup-departments'),
};

// Wiki/Documentation APIs
export const wikiAPI = {
  getOverview: () => api.get('/wiki/overview'),
  getSchema: () => api.get('/wiki/schema'),
  getApiDocs: () => api.get('/wiki/api-docs'),
  getChangelog: () => api.get('/wiki/changelog'),
  getIssues: (status) => api.get('/wiki/issues', { params: { status } }),
  getFull: () => api.get('/wiki/full'),
  // Iteration 94: search + whats-new + admin CRUD
  search: (q: string) => api.get('/wiki/search', { params: { q } }),
  whatsNew: () => api.get('/wiki/whats-new'),
  markSeen: () => api.post('/wiki/whats-new/mark-seen'),
  createChangelog: (entry: Record<string, unknown>) => api.post('/wiki/changelog', entry),
  updateChangelog: (version: string, entry: Record<string, unknown>) => api.patch(`/wiki/changelog/${version}`, entry),
  deleteChangelog: (version: string) => api.delete(`/wiki/changelog/${version}`),
  createIssue: (entry: Record<string, unknown>) => api.post('/wiki/issues', entry),
  updateIssue: (id: string, entry: Record<string, unknown>) => api.patch(`/wiki/issues/${id}`, entry),
  deleteIssue: (id: string) => api.delete(`/wiki/issues/${id}`),
};

// Audit Logs API
export const auditLogAPI = {
  list: (params?) => api.get('/audit-logs', { params }),
  getCount: (params?) => api.get('/audit-logs/count', { params }),
  getStats: (days = 30) => api.get('/audit-logs/stats', { params: { days } }),
  getActions: () => api.get('/audit-logs/actions'),
  getEntityTypes: () => api.get('/audit-logs/entity-types'),
  getUsers: () => api.get('/audit-logs/users'),
  get: (id) => api.get(`/audit-logs/${id}`),
  // Retention Policy
  getRetentionStatus: () => api.get('/audit-logs/retention/status'),
  runArchive: () => api.post('/audit-logs/retention/archive'),
  runPurge: () => api.post('/audit-logs/retention/purge'),
  // Archive
  listArchived: (params) => api.get('/audit-logs/archive', { params }),
  getArchivedCount: (params) => api.get('/audit-logs/archive/count', { params }),
  exportArchive: (params) => api.get('/audit-logs/archive/export', { params, responseType: 'blob' }),
};

export const reportAPI = {
  projectsPdf: (params?: Record<string, string>) => api.get('/reports/projects/pdf', { params, responseType: 'blob' }),
  tasksPdf: (params?: Record<string, string>) => api.get('/reports/tasks/pdf', { params, responseType: 'blob' }),
};



// Share Acks (read receipts on shared dashboard views)
export const shareAckAPI = {
  ack: (from_user_id: string, view_name?: string, filter_summary?: string) =>
    api.post('/share/ack', { from_user_id, view_name, filter_summary }),
  list: () => api.get('/share/acks'),
  clear: () => api.delete('/share/acks'),
  reply: (ack_id: string, message: string) => api.post('/share/reply', { ack_id, message }),
};
